<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { stepColor } from 'kanban/constants/reports';
import { formatNumber, formatDuration } from 'kanban/helper/reportFormatters';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

const stages = computed(() => props.data || []);

const isEmpty = computed(
  () => !stages.value.length || stages.value.every(stage => !stage.sample_size)
);

const maxP90 = computed(() =>
  Math.max(1, ...stages.value.map(stage => stage.p90_seconds || 0))
);

const barWidth = stage =>
  `${Math.max(2, ((stage.p90_seconds || 0) / maxP90.value) * 100)}%`;
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.DURATIONS.TITLE')"
    :info-text="t('KANBAN.REPORTS.DURATIONS.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="isEmpty"
    :empty-text="t('KANBAN.REPORTS.DURATIONS.EMPTY')"
    @retry="emit('retry')"
  >
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs font-medium text-n-slate-10">
            <th class="py-2 pr-4 font-medium">
              {{ t('KANBAN.REPORTS.DURATIONS.STAGE') }}
            </th>
            <th class="py-2 px-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.DURATIONS.AVG') }}
            </th>
            <th class="py-2 px-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.DURATIONS.MEDIAN') }}
            </th>
            <th class="py-2 px-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.DURATIONS.P90') }}
            </th>
            <th class="py-2 pl-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.DURATIONS.SAMPLE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(stage, index) in stages"
            :key="stage.step_id"
            class="border-t border-n-slate-3"
          >
            <td class="py-2 pr-4 min-w-[180px]">
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: stepColor(stage.color, index) }"
                  />
                  <span class="text-n-slate-12 truncate">{{ stage.name }}</span>
                </div>
                <div class="h-1.5 rounded-full bg-n-slate-3 overflow-hidden">
                  <div
                    class="h-full rounded-full"
                    :style="{
                      width: barWidth(stage),
                      backgroundColor: stepColor(stage.color, index),
                    }"
                  />
                </div>
              </div>
            </td>
            <td
              class="py-2 px-4 text-right tabular-nums text-n-slate-11 whitespace-nowrap"
            >
              {{ formatDuration(stage.avg_seconds, t) }}
            </td>
            <td
              class="py-2 px-4 text-right tabular-nums text-n-slate-12 whitespace-nowrap"
            >
              {{ formatDuration(stage.median_seconds, t) }}
            </td>
            <td
              class="py-2 px-4 text-right tabular-nums text-n-slate-12 whitespace-nowrap"
            >
              {{ formatDuration(stage.p90_seconds, t) }}
            </td>
            <td class="py-2 pl-4 text-right tabular-nums text-n-slate-10">
              {{ formatNumber(stage.sample_size, locale) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </KanbanReportWidget>
</template>
